import { graphql } from 'gatsby'
import * as JsSearch from 'js-search'
import chunk from 'lodash/chunk'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import { stemmer } from 'porter-stemmer'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { ArrowLeft, ArrowRight, Search as SearchIcon } from 'react-feather'
import Carousel from '../components/Carousel'
import Layout from '../components/Layout'
import Solution from '../components/Solution'
import WithCallToAction from '../components/WithCallToAction'
import { useLocalStorage } from '../utils/hooks'

// TODO: make a wrapper component that creates pagination for some data

export const SolutionsTemplate = ({ solutions }) => {
  const Search = new JsSearch.Search('id')
  Search.tokenizer = new JsSearch.StemmingTokenizer(
    stemmer,
    new JsSearch.SimpleTokenizer()
  )
  Search.sanitizer = new JsSearch.LowerCaseSanitizer()
  Search.indexStrategy = new JsSearch.AllSubstringsIndexStrategy()
  // featured solutions
  const featuredSolutions = solutions.filter(solution => solution.isFeatured)
  // array of unique manufacturers
  const manufacturers = uniq(
    solutions.map(solution => solution.manufacturer)
  ).filter(manufacturer => manufacturer.length > 0)
  // array of unique categories
  const categories = uniq(solutions.map(solution => solution.category)).filter(
    category => category && category.length && category.length > 0
  )
  const [currentCategory, setCurrentCategory] = useState('')
  const [currentManufacturer, setCurrentManufacturer] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useLocalStorage(
    'searchResults',
    solutions
  )

  const HITS_PER_PAGE = 9
  /* Keeps track of what page the user is on */
  const [pageNumber, setPageNumber] = useState(1)
  /* Will be an array of the current chunked data set at the page number specified above */
  const [paginatedResults, setPaginatedResults] = useState([])
  /* Holds the amount of pages of results for a given index */
  const [totalPages, setTotalPages] = useState(0)

  useEffect(() => {
    let pageNum = pageNumber
    const chunkedResults = chunk(searchResults, HITS_PER_PAGE)
    if (chunkedResults.length === 0) {
      setTotalPages(0)
      setPaginatedResults([])
      return
    }
    if (paginatedResults.length !== 0 && pageNum > chunkedResults.length) {
      setPageNumber(1)
      pageNum = 1
    }
    setTotalPages(chunkedResults.length)
    setPaginatedResults(chunkedResults[pageNum - 1])
  }, [searchResults])

  useEffect(() => {
    let pageNum = pageNumber
    const chunkedResults = chunk(searchResults, HITS_PER_PAGE)
    setTotalPages(chunkedResults.length)
    setPaginatedResults(chunkedResults[pageNum - 1])
  }, [pageNumber])

  const initializeSearch = () => {
    Search.addIndex('category')
    Search.addIndex('manufacturer')
    Search.addIndex('name')
    Search.addIndex('description')
    Search.addDocuments(solutions)
  }

  const search = (
    query = searchQuery,
    category = currentCategory,
    manufacturer = currentManufacturer
  ) => {
    initializeSearch()

    if (
      category.length === 0 &&
      manufacturer.length === 0 &&
      query.length === 0
    ) {
      setSearchResults(solutions)
      return
    }

    let combinedResults = null
    let categoryResults = null
    let manufacturerResults = null
    let queryResults = null
    if (category && manufacturer) {
      combinedResults = Search.search(`${category} ${manufacturer}`)
    } else {
      categoryResults = Search.search(category)
      manufacturerResults = Search.search(manufacturer)
    }

    queryResults = Search.search(query)

    let mergedResults

    if (combinedResults) {
      mergedResults = uniqBy([...combinedResults, ...queryResults], o => o.id)
    } else {
      mergedResults = uniqBy(
        [...manufacturerResults, ...categoryResults, ...queryResults],
        o => o.id
      )
    }

    setSearchResults(mergedResults)
  }

  useEffect(() => {
    initializeSearch()
    search()
  }, [])

  const onCategoryChange = e => {
    e.preventDefault()
    setCurrentCategory(e.target.value)
    search(searchQuery, e.target.value)
  }

  const onManufacturerChange = e => {
    e.preventDefault()
    setCurrentManufacturer(e.target.value)
    search(searchQuery, currentCategory, e.target.value)
  }

  const onSearchQueryChange = e => {
    e.preventDefault()
    setSearchQuery(e.target.value)
    search(e.target.value)
  }

  return (
    <WithCallToAction>
      <div role="main">
        <section className="section with-bg-waves">
          <div className="container content" data-testid="featured-solutions">
            <Carousel
              containScroll="keepSnaps"
              dragFree={false}
              align="start"
              inViewThreshold={0.3333333}
              slidesToScroll={1}
              autoplay
            >
              {featuredSolutions.map(solution => (
                <Solution
                  key={solution.id}
                  variant="featured"
                  solution={solution}
                />
              ))}
            </Carousel>
          </div>
        </section>
        <section className="section" id="solutions">
          <div className="container content">
            <h2 className="mb-4 section--small is-paddingless">
              All Solutions
            </h2>
            <div className="wp-block-columns has-3-columns-narrow small-gap mt-em">
              <div className="control has-icons-left flex flex-grow">
                <input
                  data-testid="solutions-search-bar"
                  className="input"
                  type="search"
                  placeholder="Search solutions..."
                  aria-label="Solutions seach bar"
                  value={searchQuery}
                  onChange={onSearchQueryChange}
                />
                <span className="icon is-medium is-left">
                  <SearchIcon />
                </span>
              </div>
              <div className="control is-narrow">
                <div className="select">
                  <select
                    value={currentCategory}
                    onChange={onCategoryChange}
                    data-testid="categories"
                    aria-label="Categories selector"
                  >
                    <option value="">Category</option>
                    {categories.map(category => (
                      <option
                        key={category}
                        value={String(category).toLowerCase()}
                      >
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="control is-narrow">
                <div className="select">
                  <select
                    value={currentManufacturer}
                    onChange={onManufacturerChange}
                    data-testid="manufacturers"
                    aria-label="Manufacturers selector"
                  >
                    <option value="">Manufacturers</option>
                    {manufacturers.map(manufacturer => (
                      <option
                        key={manufacturer}
                        value={String(manufacturer).toLowerCase()}
                      >
                        {manufacturer}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="mtb-8 space-between">
              <div>
                <strong>{searchResults.length}</strong> results found
              </div>
              <div className="horizontal-list is-vcentered fd-r align-items-center">
                <span>
                  {totalPages > 0 ? `${pageNumber} /` : ''} {totalPages}
                </span>
                <button
                  type="button"
                  tabIndex="-1"
                  rel="prev"
                  aria-label="Previous page"
                  className="button pagination-button has-border"
                  onClick={() =>
                    setPageNumber(prevPageNumber => prevPageNumber - 1)
                  }
                  disabled={pageNumber === 1}
                >
                  <ArrowLeft size={18} />
                </button>
                <button
                  type="button"
                  tabIndex="-1"
                  rel="next"
                  aria-label="Next page"
                  className="button pagination-button has-border"
                  onClick={() =>
                    setPageNumber(prevPageNumber => prevPageNumber + 1)
                  }
                  disabled={pageNumber === totalPages || totalPages === 0}
                >
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-3 gap-4"
              data-testid="solutions-search-results"
            >
              {paginatedResults && paginatedResults.length > 0 ? (
                paginatedResults.map(solution => (
                  <Solution key={solution.id} solution={solution} />
                ))
              ) : (
                <p>No solutions found</p>
              )}
            </div>
          </div>
        </section>
      </div>
    </WithCallToAction>
  )
}

const SolutionsPage = ({ data }) => {
  const {
    wordpressPage: { acf, featured_media: featuredMedia },
    allWordpressAcfOptions: { edges: solutionsEdges },
  } = data

  return (
    <Layout
      header={acf.header}
      seo={{ pageTitle: acf.header.title, pageDescription: acf.description }}
      featuredMedia={featuredMedia}
      lightText
    >
      <SolutionsTemplate solutions={solutionsEdges[0].node.options.solutions} />
    </Layout>
  )
}

SolutionsPage.propTypes = {
  data: PropTypes.shape({
    wordpressPage: PropTypes.shape({
      content: PropTypes.string,
      featured_media: PropTypes.object,
      acf: PropTypes.object,
    }),
  }),
}

export default SolutionsPage

export const pageQuery = graphql`
  fragment Solution on wordpress__acf_optionsOptionsSolutions {
    name
    id: uID
    description
    isFeatured
    isNew
    featuredPage
    category
    manufacturer
    image {
      localFile {
        childImageSharp {
          fluid(quality: 100) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  }
  query SolutionsPageById($id: String!) {
    wordpressPage(id: { eq: $id }) {
      id
      featured_media {
        localFile {
          childImageSharp {
            fluid(quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
      acf {
        header {
          title
          subtitle
        }
        description
      }
    }

    allWordpressAcfOptions {
      edges {
        node {
          options {
            solutions {
              ...Solution
            }
          }
        }
      }
    }
  }
`
